import {request} from './client.js'

/**
 * List controls visible to the current user, including live runtime state
 * (`state`/`max`/`last`). Operational endpoint used by the control view page.
 * @return {Promise<import('./types.js').ControlRecord[]>}
 */
export function listControls() {
    return request('GET', '/control')
}

/**
 * Enqueue a command for a control.
 * @param {string} id
 * @param {string} command
 * @param {Array<*>} [args] - Positional command arguments (e.g. `[50]` for openTo/setSpeed)
 */
export function sendCommand(id, command, args) {
    return request('POST', '/control/command', {
        body: {id, command, args},
        //backend accepts the command and runs the physical operation in the background,
        //responding within ~1s — this only guards against a hung request, not travel time
        signal: AbortSignal.timeout(15_000)
    })
}

/**
 * List control configuration descriptors (admin). Config-only — no runtime state.
 * @return {Promise<import('./types.js').ControlDescriptor[]>}
 */
export function listControlSettings() {
    return request('GET', '/settings/controls')
}

/**
 * Bulk-save the full controls configuration in a single atomic request (admin). The server
 * replaces the stored config with the provided array and persists it, so send complete
 * descriptors — not partials. Used to persist a reordering (each control's `order`) in one
 * call instead of one `PUT /settings/controls/:id` per moved control.
 * @param {import('./types.js').ControlDescriptor[]} controls - Full descriptors in order
 * @return {Promise<import('./types.js').ControlDescriptor[]>}
 */
export function saveControls(controls) {
    return request('PUT', '/settings/controls', {body: {controls}})
}

/**
 * Create or replace a single control by id (admin).
 * @param {string} id
 * @param {import('./types.js').ControlDescriptor} descriptor
 * @return {Promise<import('./types.js').ControlDescriptor>}
 */
export function upsertControl(id, descriptor) {
    return request('PUT', `/settings/controls/${id}`, {body: descriptor})
}

/**
 * Remove a control by id (admin).
 * @param {string} id
 * @return {Promise<{ok: number}>}
 */
export function removeControl(id) {
    return request('DELETE', `/settings/controls/${id}`)
}
